import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react/jsx-runtime";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { FaExclamationTriangle } from "react-icons/fa";
import { useTranslation } from "react-i18next";

interface SubmitErrorProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string | null;
}

export default function SubmitErrorModal({
  isOpen,
  onClose,
  message,
}: SubmitErrorProps) {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        {/* Overlay */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </TransitionChild>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all space-y-5">
                {/* Modal Header */}
                <div className="flex items-center justify-between text-gray-800 text-lg font-semibold">
                  <div className="flex items-center gap-2 text-red-600">
                    <FaExclamationTriangle size={20} />
                    <DialogTitle>{t("Submission Error")}</DialogTitle>
                  </div>
                  <button type="button" onClick={onClose} aria-label={t("Close modal")}>
                    <IoIosCloseCircleOutline
                      size={24}
                      className="text-gray-500 hover:text-red-500 transition"
                    />
                  </button>
                </div>

                {/* Modal Message */}
                <div className="text-gray-600 text-base py-1">
                  {message ||
                    t(
                      "An error occurred while submitting your claim. Please check the details and try again."
                    )}
                </div>

                {/* Action Button */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="bg-brand-color hover:bg-brand-hover text-white font-medium rounded-md px-5 py-2 transition"
                  >
                    {t("Close")}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
